import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getOffers, createOffer, updateOffer, deleteOffer } from '../../api';
import { PageHeader, EmptyState, StatusBadge, TableSkeleton } from '../../components/ui';
import { Plus, Pencil, Trash2, Tag } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/format';

const defaultForm = {
  code: '', title_en: '', title_hi: '', type: 'flat', value: '', maxDiscount: '', minAmount: '',
  purpose: 'recharge', firstTimeOnly: false, usageLimit: '', userUsageLimit: 1, isActive: true,
  startDate: '', endDate: '',
};

export default function OffersPage() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>(defaultForm);

  const { data, isLoading } = useQuery({ queryKey: ['admin-offers'], queryFn: () => getOffers().then((r) => r.data.data.offers) });

  const saveMutation = useMutation({
    mutationFn: (d: any) => editing ? updateOffer(editing._id, d) : createOffer(d),
    onSuccess: () => { toast.success('Offer saved'); qc.invalidateQueries({ queryKey: ['admin-offers'] }); closeModal(); },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteOffer(id),
    onSuccess: () => { toast.success('Offer deleted'); qc.invalidateQueries({ queryKey: ['admin-offers'] }); },
  });

  const openCreate = () => { setEditing(null); setForm(defaultForm); setModal(true); };
  const openEdit = (o: any) => {
    setEditing(o);
    setForm({ code: o.code, title_en: o.title?.en || '', title_hi: o.title?.hi || '', type: o.type, value: o.type !== 'flat' ? o.value : o.value / 100, maxDiscount: o.maxDiscount ? o.maxDiscount / 100 : '', minAmount: o.minAmount ? o.minAmount / 100 : '', purpose: o.purpose, firstTimeOnly: o.firstTimeOnly, usageLimit: o.usageLimit || '', userUsageLimit: o.userUsageLimit, isActive: o.isActive, startDate: o.startDate?.slice(0, 10) || '', endDate: o.endDate?.slice(0, 10) || '' });
    setModal(true);
  };
  const closeModal = () => { setModal(false); setEditing(null); setForm(defaultForm); };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: any = {
      code: form.code.toUpperCase(),
      title: { en: form.title_en, hi: form.title_hi },
      type: form.type,
      value: form.type === 'flat' ? Math.round(Number(form.value) * 100) : Number(form.value),
      purpose: form.purpose,
      firstTimeOnly: form.firstTimeOnly,
      userUsageLimit: form.userUsageLimit,
      isActive: form.isActive,
    };
    if (form.maxDiscount) payload.maxDiscount = Math.round(Number(form.maxDiscount) * 100);
    if (form.minAmount) payload.minAmount = Math.round(Number(form.minAmount) * 100);
    if (form.usageLimit) payload.usageLimit = Number(form.usageLimit);
    if (form.startDate) payload.startDate = form.startDate;
    if (form.endDate) payload.endDate = form.endDate;
    saveMutation.mutate(payload);
  };

  const offers = data || [];

  return (
    <div className="p-6">
      <PageHeader title="Coupons & Offers" subtitle="First-consultation offers, wallet bonuses and campaigns">
        <button onClick={openCreate} className="btn-primary"><Plus size={15} /> Create Offer</button>
      </PageHeader>
      <div className="card overflow-hidden">
        {isLoading ? <div className="p-5"><TableSkeleton rows={5} cols={7} /></div> : offers.length === 0 ? (
          <EmptyState message="No offers yet" icon={<Tag size={32} />} />
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="table-th">Code</th>
                <th className="table-th">Title</th>
                <th className="table-th">Type</th>
                <th className="table-th">Value</th>
                <th className="table-th">Purpose</th>
                <th className="table-th">Used</th>
                <th className="table-th">Status</th>
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {offers.map((o: any) => (
                <tr key={o._id} className="table-row">
                  <td className="table-td font-mono font-bold text-primary-600 text-xs">{o.code}</td>
                  <td className="table-td text-xs">{o.title?.en}</td>
                  <td className="table-td"><span className="badge badge-blue capitalize">{o.type}</span></td>
                  <td className="table-td text-xs">{o.type === 'flat' ? formatCurrency(o.value) : o.type === 'percent' ? `${o.value}%` : `₹${o.value / 100} cashback`}</td>
                  <td className="table-td"><span className="badge badge-purple capitalize">{o.purpose}</span></td>
                  <td className="table-td text-xs">{o.usedCount}{o.usageLimit ? `/${o.usageLimit}` : ''}</td>
                  <td className="table-td"><StatusBadge status={o.isActive ? 'active' : 'inactive'} /></td>
                  <td className="table-td">
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(o)} className="p-1.5 rounded hover:bg-gray-100 text-blue-500"><Pencil size={13} /></button>
                      <button onClick={() => { if (window.confirm('Delete?')) deleteMutation.mutate(o._id); }} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-gray-900 mb-4">{editing ? 'Edit Offer' : 'Create Offer'}</h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Coupon Code *</label>
                  <input className="input uppercase" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SAVE50" required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type *</label>
                  <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    <option value="flat">Flat (₹)</option>
                    <option value="percent">Percent (%)</option>
                    <option value="cashback">Cashback (₹)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Title (English) *</label>
                <input className="input" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} required />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Title (Hindi)</label>
                <input className="input" value={form.title_hi} onChange={(e) => setForm({ ...form, title_hi: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Value {form.type === 'percent' ? '(%)' : '(₹)'} *</label>
                  <input type="number" className="input" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} required />
                </div>
                {form.type === 'percent' && <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Max Discount (₹)</label>
                  <input type="number" className="input" value={form.maxDiscount} onChange={(e) => setForm({ ...form, maxDiscount: e.target.value })} />
                </div>}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Min Amount (₹)</label>
                  <input type="number" className="input" value={form.minAmount} onChange={(e) => setForm({ ...form, minAmount: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Purpose</label>
                  <select className="input" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}>
                    <option value="all">All</option>
                    <option value="recharge">Recharge</option>
                    <option value="consultation">Consultation / first session</option>
                    <option value="appointment">Appointment</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Usage Limit (total)</label>
                  <input type="number" className="input" value={form.usageLimit} onChange={(e) => setForm({ ...form, usageLimit: e.target.value })} placeholder="Unlimited" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                  <input type="date" className="input" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                  <input type="date" className="input" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> Active</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.firstTimeOnly} onChange={(e) => setForm({ ...form, firstTimeOnly: e.target.checked })} /> First-time users only</label>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={closeModal} className="btn-secondary flex-1 justify-center">Cancel</button>
                <button type="submit" disabled={saveMutation.isPending} className="btn-primary flex-1 justify-center">{saveMutation.isPending ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
